import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../../lib/adminApi";
import { TInput, Sel, Toggle, Btn, Card } from "../ui";

const EMPTY_METHOD = { id: "", name: "Standard", rateType: "FLAT", amount: 0, freeAboveAmount: null, active: false };

export default function ShippingTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    adm.settings().then((s) => setCfg(s.shipping || { enabled: false, zones: [], categoryRules: [], handlingTime: "" })).catch(() => {});
  }, []);
  if (!cfg) return <p className="font-mono text-xs text-white/40">LOADING…</p>;

  const set = (k, v) => setCfg((x) => ({ ...x, [k]: v }));
  const setZone = (zi, patch) => set("zones", cfg.zones.map((z, i) => (i === zi ? { ...z, ...patch } : z)));
  const setMethod = (zi, mi, patch) =>
    setZone(zi, { methods: cfg.zones[zi].methods.map((m, i) => (i === mi ? { ...m, ...patch } : m)) });

  const save = async () => {
    setSaving(true);
    try {
      await adm.saveSettings({ shipping: cfg });
      toast.success("Shipping configuration saved.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="store-shipping-tab">
      <Card className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Toggle label={cfg.enabled ? "SHIPPING ENABLED — RATES APPLY AT CHECKOUT" : "SHIPPING DISABLED — NO CHARGES AT CHECKOUT"}
              checked={!!cfg.enabled} onChange={(v) => set("enabled", v)} testid="shipping-enabled" />
            <p className="font-mono text-[8px] tracking-[0.15em] text-white/35 mt-3 max-w-2xl">
              DIGITAL PRODUCTS (DIGITAL ALBUMS, SINGLES, RINGTONES) NEVER RECEIVE SHIPPING CHARGES.
              KEEP DISABLED UNTIL REAL RATES ARE ENTERED. ANY TEST RATE MUST BE CLEARLY LABELLED "TEST".
            </p>
          </div>
          <Btn onClick={save} disabled={saving} testid="shipping-save">{saving ? "SAVING…" : "SAVE SHIPPING"}</Btn>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <TInput label="HANDLING / PROCESSING TIME (SHOWN TO BUYERS LATER)" value={cfg.handlingTime}
            onChange={(v) => set("handlingTime", v)} testid="shipping-handling" />
        </div>
      </Card>

      {(cfg.zones || []).map((z, zi) => (
        <Card key={z.id || zi} className="mb-4" data-testid={`shipping-zone-${z.id}`}>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <TInput label="ZONE NAME" value={z.name} onChange={(v) => setZone(zi, { name: v })} />
            <TInput label="COUNTRIES (ISO CODES, COMMA — e.g. US,CA)" value={(z.countries || []).join(",")}
              onChange={(v) => setZone(zi, { countries: v.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
              testid={`shipping-zone-${z.id}-countries`} />
          </div>
          {(z.methods || []).map((m, mi) => (
            <div key={m.id || mi} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end border-t border-white/10 pt-3 mt-3">
              <TInput label="METHOD (e.g. STANDARD)" value={m.name} onChange={(v) => setMethod(zi, mi, { name: v })} />
              <Sel label="RATE TYPE" value={m.rateType} onChange={(v) => setMethod(zi, mi, { rateType: v })}
                options={[{ v: "FLAT", l: "FLAT RATE" }, { v: "FREE", l: "FREE" }]} />
              {m.rateType === "FLAT" && (
                <TInput label="RATE (CENTS)" value={m.amount} onChange={(v) => setMethod(zi, mi, { amount: Number(v) || 0 })}
                  type="number" testid={`shipping-${z.id}-${m.id}-amount`} />
              )}
              <TInput label="FREE ABOVE (CENTS, OPTIONAL)" value={m.freeAboveAmount}
                onChange={(v) => setMethod(zi, mi, { freeAboveAmount: v === "" ? null : Number(v) })} type="number" />
              <div className="flex items-end pb-1">
                <Toggle label={m.active ? "ACTIVE" : "OFF"} checked={!!m.active}
                  onChange={(v) => setMethod(zi, mi, { active: v })} testid={`shipping-${z.id}-${m.id}-active`} />
              </div>
              <button onClick={() => setZone(zi, { methods: z.methods.filter((_, i) => i !== mi) })}
                className="text-red-400/70 hover:text-red-400 text-xs pb-3 text-left">REMOVE</button>
            </div>
          ))}
          <Btn variant="ghost" className="mt-4"
            onClick={() => setZone(zi, { methods: [...(z.methods || []), { ...EMPTY_METHOD, id: crypto.randomUUID().slice(0, 8) }] })}>
            ADD METHOD
          </Btn>
        </Card>
      ))}

      <p className="font-mono text-[8px] tracking-[0.15em] text-white/30 mt-2 max-w-2xl">
        PRODUCT-SPECIFIC OVERRIDES LIVE ON EACH PRODUCT (SHIPPING OVERRIDE). WEIGHT & DIMENSIONS ARE STORED
        PER PRODUCT FOR FUTURE CARRIER-CALCULATED RATES — CHECKOUT WILL NOT NEED A REBUILD.
        FREE-SHIPPING PROMO CODES ONLY TAKE EFFECT ONCE AN APPLICABLE RATE EXISTS HERE.
      </p>
    </div>
  );
}
